import {
  useCreateMyReservationMutation,
  useGetMyEstimateResponseInfoMinimalQuery,
} from '@massilia/queries'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { z } from 'zod'

import { LoadingSkeleton } from '@/routes/reservation/-components/loading-skeleton'
import { ReservationError } from '@/routes/reservation/-components/reservation-error'
import { ReservationForm } from '@/routes/reservation/-components/reservation-form'

const searchSchema = z.object({
  withInsurance: z.boolean().optional().default(false),
})

/**
 * Réservation d'une proposition depuis l'espace client.
 *
 * Même formulaire que le parcours public : seules les sources de données et la
 * mutation changent, toutes deux derrière la vérification d'appartenance.
 */
export const Route = createFileRoute(
  '/_authenticated/_layout/mes-devis_/$estimateResponseId/reserver'
)({
  component: ReserverPage,
  errorComponent: ReservationError,
  staticData: {
    hero: {
      subtitle: 'Vérifiez les informations des voyageurs pour finaliser votre demande',
      title: 'Demande de réservation',
    },
  },
  validateSearch: searchSchema,
})

export function ReserverPage() {
  const { estimateResponseId } = Route.useParams()
  const { withInsurance } = Route.useSearch()
  const navigate = useNavigate()
  const createReservation = useCreateMyReservationMutation()

  const {
    data: estimateData,
    error,
    isLoading,
  } = useGetMyEstimateResponseInfoMinimalQuery({ id: estimateResponseId })

  if (isLoading) return <LoadingSkeleton />
  if (error || !estimateData) return <ReservationError error={error} />

  return (
    // `max-w-5xl` et non `3xl` : à 768 px les cartes passagers tiennent à deux
    // par ligne et tronquaient les noms, alors que la page laissait 300 px de
    // marge vide de chaque côté. Marge latérale réduite sur mobile.
    <main className="mx-auto flex w-full max-w-5xl flex-1 flex-col bg-white px-4 py-6 md:px-8 md:py-10">
      <ReservationForm
        estimateData={estimateData}
        estimateResponseId={estimateResponseId}
        onSuccess={() => {
          toast.success('Votre demande de réservation a bien été transmise', {
            description: 'Nos équipes reviennent vers vous très vite.',
          })
          void navigate({ to: '/mes-devis' })
        }}
        submit={async (request) =>
          createReservation.mutateAsync({ ...request, responseId: estimateResponseId })
        }
        withInsurance={withInsurance}
      />
    </main>
  )
}
